import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PortalV2Layout from './PortalV2Layout';
import { useAuth } from '../contexts/AuthContext';
import { Search, Receipt, Send, RefreshCw, Trash2, Mail, AlertCircle, CheckCircle, Calendar } from 'lucide-react';

const fmtUSD = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// NET30 aging
const getAging = (invoice, now = new Date()) => {
  const issued = invoice.issued_on ? new Date(invoice.issued_on) : null;
  if (!issued) return { days: 0, category: 'current', label: 'Current', color: 'blue' };
  const due = new Date(issued); due.setDate(due.getDate() + 30);
  const days = Math.floor((now.getTime() - due.getTime()) / 86400000);
  if (days <= 0) return { days: 0, category: 'current', label: 'Current', color: 'blue' };
  if (days <= 30) return { days, category: '1-30', label: `${days} days late`, color: 'amber' };
  if (days <= 60) return { days, category: '31-60', label: `${days} days late`, color: 'orange' };
  if (days <= 90) return { days, category: '61-90', label: `${days} days late`, color: 'red' };
  if (days <= 120) return { days, category: '91-120', label: `${days} days late`, color: 'rose' };
  return { days, category: '121+', label: `${days} days late`, color: 'darkred' };
};

const colorMap = {
  blue: '#60a5fa', amber: 'var(--pv2-amber-text)', orange: '#fb923c',
  red: 'var(--pv2-red-bright)', rose: '#f43f5e', darkred: '#dc2626'
};

const AGING_OPTIONS = [
  { value: 'all', label: 'All Outstanding' },
  { value: 'current', label: 'Current (within 30 days)' },
  { value: '1-30', label: 'Past Due 1-30 days' },
  { value: '31-60', label: 'Past Due 31-60 days' },
  { value: '61-90', label: 'Past Due 61-90 days' },
  { value: '91-120', label: 'Past Due 91-120 days' },
  { value: '121+', label: 'Past Due 121+ days' },
];

const AdminV2Invoices = () => {
  const { authFetch } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [agingFilter, setAgingFilter] = useState('all');
  const [msg, setMsg] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingBulk, setSendingBulk] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [reminderStats, setReminderStats] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/admin/invoices');
      if (res.ok) {
        const d = await res.json();
        setInvoices(d.invoices || d || []);
      } else setError('Failed to load invoices');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const loadStats = useCallback(async () => {
    try {
      const r = await authFetch('/api/admin/reminders/stats');
      if (r.ok) setReminderStats(await r.json());
    } catch {}
  }, [authFetch]);

  useEffect(() => { load(); loadStats(); /* eslint-disable-next-line */ }, []);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 5000); };

  const sendReminder = async (id, email) => {
    setSendingId(id);
    try {
      const res = await authFetch(`/api/admin/reminders/send/${id}`, { method: 'POST' });
      showMsg(res.ok ? 'green' : 'red', res.ok ? `Reminder sent to ${email || 'client'}.` : 'Could not send reminder.');
      await load(); await loadStats();
    } finally { setSendingId(null); }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await authFetch('/api/admin/reminders/send-digest', { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        showMsg('green', `Digest sent. ${d.summary?.total_overdue || 0} overdue invoices reported.`);
      } else showMsg('red', 'Digest failed.');
    } finally { setSendingDigest(false); }
  };

  const sendBulk = async () => {
    if (!window.confirm(`Send reminders to all overdue invoices that haven't been reminded in the last 7 days?\n\nThis runs in the background.`)) return;
    setSendingBulk(true);
    try {
      const res = await authFetch('/api/admin/reminders/send-bulk', { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (d.already_running) { showMsg('amber', d.message || 'Bulk reminders already running.'); }
      else if (d.background) { showMsg('green', `Sending ${d.total_overdue || 0} reminders in the background…`); }
      else { showMsg('green', d.message || 'Reminder job started.'); }
      await loadStats();
    } catch { showMsg('red', 'Bulk send failed.'); }
    finally { setSendingBulk(false); }
  };

  const refresh = async (id) => {
    setRefreshingId(id);
    try {
      const res = await authFetch(`/api/admin/invoices/${id}/refresh`, { method: 'POST' });
      if (res.ok) {
        const d = await res.json();
        showMsg('green', `Refreshed — Balance: $${d.balance_due}, Paid: $${d.total_paid}`);
      }
      await load();
    } finally { setRefreshingId(null); }
  };

  const deleteInvoice = async (id, jobNum) => {
    if (!window.confirm(`Delete invoice for Job #${jobNum || id} from the portal?\n\nThis only removes it from the portal — it does not affect ServeManager.`)) return;
    setDeletingId(id);
    try {
      const res = await authFetch(`/api/admin/invoices/${id}`, { method: 'DELETE' });
      showMsg(res.ok ? 'green' : 'red', res.ok ? 'Invoice deleted.' : 'Delete failed.');
      await load();
    } finally { setDeletingId(null); }
  };

  // Summary stats
  const summary = useMemo(() => {
    const now = new Date();
    let outstanding = 0, paid = 0, currentCount = 0, pastDueCount = 0, newThisMonth = 0, newThisMonthAmount = 0;
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    invoices.forEach(inv => {
      const balance = parseFloat(inv.balance_due || 0);
      const totalPaid = parseFloat(inv.total_paid || 0);
      const total = parseFloat(inv.total || 0);
      outstanding += balance; paid += totalPaid;
      if (inv.issued_on && new Date(inv.issued_on) >= firstOfMonth) { newThisMonth++; newThisMonthAmount += total; }
      if (balance > 0) {
        const a = getAging(inv, now);
        if (a.days <= 0) currentCount++; else pastDueCount++;
      }
    });
    return { outstanding, paid, total: invoices.length, currentCount, pastDueCount, newThisMonth, newThisMonthAmount };
  }, [invoices]);

  // Filtered & grouped (sorted by issued date descending — most recent first)
  const { current, pastDue } = useMemo(() => {
    const now = new Date();
    const cur = [], pd = [];
    invoices.forEach(inv => {
      const balance = parseFloat(inv.balance_due || 0);
      if (balance <= 0) return;
      const a = getAging(inv, now);
      if (agingFilter !== 'all' && a.category !== agingFilter) return;
      if (search) {
        const q = search.toLowerCase();
        if (!String(inv.servemanager_job_number || '').toLowerCase().includes(q)
            && !String(inv.client_email || '').toLowerCase().includes(q)) return;
      }
      const enriched = { ...inv, aging: a };
      if (a.category === 'current') cur.push(enriched);
      else pd.push(enriched);
    });
    // Most recent issued first
    const byIssuedDesc = (a, b) => new Date(b.issued_on || 0).getTime() - new Date(a.issued_on || 0).getTime();
    cur.sort(byIssuedDesc);
    pd.sort(byIssuedDesc);
    return { current: cur, pastDue: pd };
  }, [invoices, agingFilter, search]);

  const InvoiceRow = ({ inv }) => {
    const balance = parseFloat(inv.balance_due || 0);
    const total = parseFloat(inv.total || 0);
    const totalPaid = parseFloat(inv.total_paid || 0);
    const a = inv.aging;
    const isOverdue = a.days > 0;
    const canRemind = inv.client_email && a.category !== 'current';

    return (
      <tr data-testid={`pv2-invoice-row-${inv.servemanager_job_number}`}>
        <td>
          <Link to={`/portal/jobs/${inv.servemanager_job_number || inv.servemanager_job_id || ''}`} className="pv2-table-job-num">{inv.servemanager_job_number || (inv.id?.slice(0,8))}</Link>
          <div style={{ fontSize: 11, color: 'var(--pv2-text-muted)', marginTop: 2 }}>{inv.client_email || '—'}</div>
          {inv.portal_paid && balance > 0 && (
            <div
              data-testid={`pv2-portal-paid-badge-${inv.servemanager_job_number}`}
              title="Client already paid this through the portal — zero this invoice out in ServeManager."
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, padding: '2px 7px', background: 'rgba(74,222,128,0.10)', border: '1px solid rgba(74,222,128,0.35)', color: 'var(--pv2-green-text)', fontSize: 10, fontFamily: 'JetBrains Mono, monospace', letterSpacing: 0.4, textTransform: 'uppercase' }}
            >
              <CheckCircle size={11} strokeWidth={2} /> Paid via portal · needs zeroing
            </div>
          )}
        </td>
        <td>
          <span style={{ color: colorMap[a.color], fontWeight: 600, fontSize: 11, fontFamily: 'JetBrains Mono, monospace', letterSpacing: 0.5, textTransform: 'uppercase' }}>
            {a.label}
          </span>
        </td>
        <td className="pv2-mono" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>{fmtDate(inv.issued_on)}</td>
        <td style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 500 }}>{fmtUSD(total)}</div>
          {totalPaid > 0 && <div style={{ fontSize: 11, color: 'var(--pv2-text-subtle)', marginTop: 2 }}>Paid {fmtUSD(totalPaid)}</div>}
        </td>
        <td style={{ textAlign: 'right', fontWeight: 600, color: colorMap[a.color] }}>{fmtUSD(balance)}</td>
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          {canRemind && (
            <button onClick={() => sendReminder(inv.id, inv.client_email)} disabled={sendingId === inv.id} title={`Send reminder to ${inv.client_email}`}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'transparent', border: 0, color: 'var(--pv2-amber-text)', cursor: 'pointer' }}>
              <Mail size={14} strokeWidth={1.5} />
            </button>
          )}
          <button onClick={() => refresh(inv.id)} disabled={refreshingId === inv.id} title="Refresh from ServeManager"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'transparent', border: 0, color: 'var(--pv2-text-muted)', cursor: 'pointer', marginLeft: 2 }}>
            <RefreshCw size={14} strokeWidth={1.5} className={refreshingId === inv.id ? 'pv2-skeleton' : ''} />
          </button>
          <button onClick={() => deleteInvoice(inv.id, inv.servemanager_job_number)} disabled={deletingId === inv.id} title="Delete from portal"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, background: 'transparent', border: 0, color: 'var(--pv2-red-bright)', cursor: 'pointer', marginLeft: 2 }}>
            <Trash2 size={14} strokeWidth={1.5} />
          </button>
        </td>
      </tr>
    );
  };

  return (
    <PortalV2Layout
      eyebrow="Billing"
      title="Invoices"
      subtitle="NET30 Payment Terms · Outstanding balances, aging, and reminders."
      crumbs={<><span style={{ opacity: 0.5 }}>Admin /</span> <strong style={{ marginLeft: 6 }}>Invoices</strong></>}
      action={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={sendDigest} disabled={sendingDigest} className="pv2-btn pv2-btn-ghost" data-testid="pv2-send-digest">
            <Mail size={14} strokeWidth={1.5} className={sendingDigest ? 'pv2-skeleton' : ''} /> {sendingDigest ? 'Sending…' : 'Send Digest'}
          </button>
          <button onClick={sendBulk} disabled={sendingBulk || !reminderStats?.needs_reminder} className="pv2-btn pv2-btn-primary" data-testid="pv2-send-bulk-reminders" title={reminderStats ? `${reminderStats.needs_reminder} invoices need reminders` : 'Loading…'}>
            <Send size={14} strokeWidth={1.5} className={sendingBulk ? 'pv2-skeleton' : ''} /> {sendingBulk ? 'Sending…' : `Send Reminders (${reminderStats?.needs_reminder || 0})`}
          </button>
          <button onClick={load} disabled={loading} className="pv2-btn pv2-btn-ghost" data-testid="pv2-refresh-admin-invoices">
            <RefreshCw size={14} strokeWidth={1.5} className={loading ? 'pv2-skeleton' : ''} /> Refresh
          </button>
        </div>
      }
    >
      {error && <div className="pv2-alert pv2-alert-red"><AlertCircle size={14} /><span>{error}</span></div>}
      {msg && <div className={`pv2-alert pv2-alert-${msg.type === 'green' ? 'green' : msg.type === 'amber' ? 'amber' : 'red'}`}>{msg.type === 'green' ? <CheckCircle size={14} /> : <AlertCircle size={14} />}<span>{msg.text}</span></div>}

      {/* Summary stat cards */}
      <section className="pv2-stats-grid" style={{ marginBottom: 32 }}>
        <div className="pv2-stat">
          <div className="pv2-stat-label">Outstanding</div>
          <div className="pv2-stat-value" style={{ color: 'var(--pv2-red-bright)' }}>{fmtUSD(summary.outstanding)}</div>
          <div className="pv2-stat-meta">{summary.pastDueCount} PAST DUE, {summary.currentCount} CURRENT</div>
        </div>
        <div className="pv2-stat">
          <div className="pv2-stat-label">Current</div>
          <div className="pv2-stat-value" style={{ color: '#60a5fa' }}>{summary.currentCount}</div>
          <div className="pv2-stat-meta">WITHIN PAYMENT TERMS</div>
        </div>
        <div className="pv2-stat">
          <div className="pv2-stat-label">Total Invoices</div>
          <div className="pv2-stat-value">{summary.total}</div>
          <div className="pv2-stat-meta">ALL TIME</div>
        </div>
        <div className="pv2-stat">
          <div className="pv2-stat-label">New This Month</div>
          <div className="pv2-stat-value" style={{ color: 'var(--pv2-green-text)' }}>{summary.newThisMonth}</div>
          <div className="pv2-stat-meta">{fmtUSD(summary.newThisMonthAmount)} ISSUED</div>
        </div>
      </section>

      {/* Search + filter */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        <div style={{ position: 'relative', flex: '1 1 280px' }}>
          <Search size={14} strokeWidth={1.5} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--pv2-text-subtle)' }} />
          <input className="pv2-input" placeholder="Search by Job # or client email…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ paddingLeft: 36 }} data-testid="pv2-admin-inv-search" />
        </div>
        <select className="pv2-select" value={agingFilter} onChange={(e) => setAgingFilter(e.target.value)} style={{ flex: '0 0 240px' }} data-testid="pv2-aging-filter">
          {AGING_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Current section */}
      {(agingFilter === 'all' || agingFilter === 'current') && current.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <h3 className="pv2-display" style={{ fontSize: 18, color: '#60a5fa' }}>Current Invoices</h3>
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'baseline', gap: 8, color: '#60a5fa' }}>
              <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, fontWeight: 700 }}>{current.length}</span>
              <span className="pv2-mono-sm" style={{ color: '#60a5fa' }}>Within 30-day payment terms</span>
            </span>
          </div>
          <div className="pv2-table-wrap" style={{ borderColor: 'rgba(96,165,250,0.3)' }}>
            <table className="pv2-table">
              <thead>
                <tr><th>Job # / Client</th><th>Aging</th><th>Issued</th><th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>Balance</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
              </thead>
              <tbody>{current.map(inv => <InvoiceRow key={inv.id} inv={inv} />)}</tbody>
            </table>
          </div>
        </section>
      )}

      {/* Past due section */}
      {(agingFilter === 'all' || agingFilter !== 'current') && pastDue.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <h3 className="pv2-display" style={{ fontSize: 18, color: 'var(--pv2-red-bright)' }}>Past Due Invoices</h3>
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'baseline', gap: 8, color: 'var(--pv2-red-bright)' }}>
              <span style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, fontWeight: 700 }}>{pastDue.length}</span>
              <span className="pv2-mono-sm" style={{ color: 'var(--pv2-red-bright)' }}>Over 30 days since issued</span>
            </span>
          </div>
          <div className="pv2-table-wrap" style={{ borderColor: 'rgba(248,113,113,0.3)' }}>
            <table className="pv2-table" data-testid="pv2-admin-inv-table">
              <thead>
                <tr><th>Job # / Client</th><th>Aging</th><th>Issued</th><th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>Balance</th><th style={{ textAlign: 'right' }}>Actions</th></tr>
              </thead>
              <tbody>{pastDue.map(inv => <InvoiceRow key={inv.id} inv={inv} />)}</tbody>
            </table>
          </div>
        </section>
      )}

      {!loading && current.length === 0 && pastDue.length === 0 && (
        <div className="pv2-card" style={{ padding: 48, textAlign: 'center' }}>
          <Receipt size={28} style={{ opacity: 0.3, marginBottom: 12 }} />
          <div style={{ fontSize: 14, color: 'var(--pv2-text-muted)' }}>{agingFilter === 'all' ? 'All invoices are paid!' : 'No invoices match this filter.'}</div>
        </div>
      )}
      {loading && <div className="pv2-card" style={{ padding: 48, textAlign: 'center' }}>Loading invoices…</div>}

      {/* NET30 helper */}
      <div className="pv2-card" style={{ marginTop: 24, padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Calendar size={14} strokeWidth={1.5} color="var(--pv2-text-muted)" />
          <span className="pv2-mono-tag">NET30 Payment Terms</span>
        </div>
        <p style={{ fontSize: 13, color: 'var(--pv2-text-muted)', lineHeight: 1.6 }}>
          Invoices are due 30 days from the issued date. Day 31 and beyond is considered past due. Color coding indicates aging:{' '}
          <span style={{ color: '#60a5fa', fontWeight: 500 }}>Blue = Current</span>,{' '}
          <span style={{ color: 'var(--pv2-amber-text)', fontWeight: 500 }}>Amber = 1-30 days late</span>,{' '}
          <span style={{ color: '#fb923c', fontWeight: 500 }}>Orange = 31-60 days</span>,{' '}
          <span style={{ color: 'var(--pv2-red-bright)', fontWeight: 500 }}>Red = 61+ days</span>.
        </p>
      </div>
    </PortalV2Layout>
  );
};

export default AdminV2Invoices;
